import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  useBudgetShares,
  useInviteToShare,
  useRevokeShare,
} from '@/hooks/useShares';
import { ShareService } from '@/services/ShareService';
import { formatDate } from '@/lib/utils';

interface BudgetShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  budgetId: string;
  budgetName?: string;
}

export function BudgetShareDialog({
  open,
  onOpenChange,
  budgetId,
  budgetName,
}: BudgetShareDialogProps) {
  const [invitee, setInvitee] = React.useState('');
  const { data: shares = [], isLoading } = useBudgetShares(budgetId);
  const invite = useInviteToShare();
  const revoke = useRevokeShare();

  React.useEffect(() => {
    if (!open) setInvitee('');
  }, [open]);

  const pending = shares.filter((s) => !s.accepted);
  const active = shares.filter((s) => s.accepted);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitee.trim()) return;
    await invite.mutateAsync({
      budgetId,
      invitee: invitee.trim(),
      role: 'EDITOR',
    });
    setInvitee('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share budget</DialogTitle>
          <DialogDescription>
            {budgetName
              ? `Invite collaborators to "${budgetName}".`
              : 'Invite collaborators to view and edit this budget.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <form onSubmit={handleInvite} className="flex gap-2">
            <Input
              type="text"
              placeholder="Email or name"
              value={invitee}
              onChange={(e) => setInvitee(e.target.value)}
              required
              className="flex-1"
            />
            <Button type="submit" loading={invite.isPending}>
              Invite
            </Button>
          </form>
          <p className="text-xs text-muted-foreground">
            Collaborators can view and edit this budget. Only you can delete it or manage sharing.
          </p>

          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading shares…</p>
          ) : (
            <>
              {pending.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-foreground">Pending invitations</h3>
                  <ul className="space-y-2">
                    {pending.map((share) => (
                      <li
                        key={share.id}
                        className="flex items-center justify-between gap-2 rounded-md border p-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{share.userEmail}</p>
                          <p className="text-xs text-muted-foreground">
                            Sent {formatDate(share.createdAt)} · Awaiting response
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => revoke.mutate(share.id)}
                          loading={revoke.isPending}
                        >
                          Cancel
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {active.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-foreground">Active collaborators</h3>
                  <ul className="space-y-2">
                    {active.map((share) => (
                      <li
                        key={share.id}
                        className="flex items-center justify-between gap-2 rounded-md border p-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{share.userEmail}</p>
                          <p className="text-xs text-muted-foreground">
                            {ShareService.getRoleName(share.role)} · Joined {formatDate(share.createdAt)}
                          </p>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => revoke.mutate(share.id)}
                          loading={revoke.isPending}
                        >
                          Revoke
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {pending.length === 0 && active.length === 0 && (
                <p className="text-sm text-muted-foreground">No collaborators yet.</p>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
